import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import {
  getFirestore,
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import app from '../firebase.js';

const AgentContext = createContext({});

const CURRENT_AGENT_STORAGE_KEY = 'blueprint_current_agent_id';

export const AgentProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const db = getFirestore(app);

  const [agents, setAgents] = useState([]);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Listen to user's agents collection
  useEffect(() => {
    if (!currentUser) {
      setAgents([]);
      setLoading(false);
      return;
    }

    const unsub = onSnapshot(
      query(collection(db, 'agents'), where('admins', 'array-contains', currentUser.uid)), 
      snap => {
        const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setAgents(rows);
      
      // If we have agents and no current agent is set, try to load from localStorage
      if (rows.length > 0 && !currentAgent) {
        const savedAgentId = localStorage.getItem(CURRENT_AGENT_STORAGE_KEY);
        if (savedAgentId) {
          const savedAgent = rows.find(agent => agent.id === savedAgentId);
          if (savedAgent) {
            setCurrentAgent(savedAgent);
          }
        }
      }
      setLoading(false);
    });
    return unsub;
  }, [db, currentUser, currentAgent]);

  // Set current agent and persist to localStorage
  const selectAgent = useCallback((agent) => {
    setCurrentAgent(agent);
    if (agent) {
      localStorage.setItem(CURRENT_AGENT_STORAGE_KEY, agent.id);
    } else {
      localStorage.removeItem(CURRENT_AGENT_STORAGE_KEY);
    }
  }, []);

  // Get current agent ID from localStorage (useful for OAuth flow)
  const getCurrentAgentId = useCallback(() => {
    return localStorage.getItem(CURRENT_AGENT_STORAGE_KEY);
  }, []);

  // Clear current agent
  const clearCurrentAgent = useCallback(() => {
    setCurrentAgent(null);
    localStorage.removeItem(CURRENT_AGENT_STORAGE_KEY);
  }, []);

  const value = {
    agents,
    currentAgent,
    loading,
    selectAgent,
    getCurrentAgentId,
    clearCurrentAgent,
  };

  return (
    <AgentContext.Provider value={value}>
      {children}
    </AgentContext.Provider>
  );
};

export const useAgent = () => useContext(AgentContext);
